import React, { useState } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { FaPaperPlane, FaRobot, FaUser } from 'react-icons/fa';

const CareerAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const formatText = (text) => {
    return text
      .split('\n')
      .map(line => {
        // Format bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Format bullet points
        line = line.replace(/^•\s/g, '<br/>• ');
        return line;
      })
      .join('<br/>');
  };

  const renderMessage = (message) => {
    if (message.sender === 'user') {
      return <div className="whitespace-pre-wrap">{message.text}</div>;
    }
    
    return (
      <div 
        className="markdown-content"
        dangerouslySetInnerHTML={{ __html: formatText(message.text) }}
      />
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);

    try {
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      const prompt = {
        contents: [{
          parts: [{
            text: `You are a specialized career advisor AI assistant. When providing advice, follow these formatting rules:
            1. Use clear section headings in bold, like:
               "Building Your Resume"
            
            2. Use numbered lists for main sections:
               1. First Point
               2. Second Point
            
            3. Use bullet points (•) for sub-items:
               • First sub-item
               • Second sub-item
            
            4. Format key points with:
               • Clear line breaks between sections
               • No asterisks (*) for formatting
               • Simple indentation for readability
            
            5. End with a "Quick Tips" section containing 3-4 actionable items
            
            If the question is NOT career-related, respond with:
            "I am a career-focused AI assistant. I can only help with questions about job searching, resume writing, interview preparation, and career development. Please ask me something related to these topics."

            Question: ${userMessage}`
          }]
        }]
      };

      const result = await model.generateContent(prompt);
      const response = await result.response.text();

      setMessages(prev => [...prev, { 
        text: response,
        sender: 'bot' 
      }]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setMessages(prev => [...prev, { 
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 h-[600px] flex flex-col">
      <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
        <h2 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
          <FaRobot className="text-indigo-600" />
          Career Assistant AI
        </h2>
        <p className="text-sm text-indigo-700 dark:text-indigo-200">
          Ask me about job search, resume tips, interview prep, or career advice!
        </p>
      </div>

      <div className="flex-1 overflow-y-auto mb-4 space-y-4">
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex items-start gap-2 max-w-[80%] ${
              message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                message.sender === 'user'
                  ? 'bg-indigo-100 text-indigo-600'
                  : 'bg-green-100 text-green-600'
              }`}>
                {message.sender === 'user' ? <FaUser /> : <FaRobot />}
              </div>
              <div className={`p-3 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              }`}>
                {renderMessage(message)}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
              <div className="animate-pulse flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSendMessage} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about career advice, resume tips, or job search..."
          className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaPaperPlane />
        </button>
      </form>
    </div>
  );
};

export default CareerAssistant;